import { useQuery } from '@tanstack/react-query'
import { BaseBookingModel } from '@xeetr/types/models'
import { useContext } from 'react'

import { useErrorHandler } from './common/useErrorHandler'
import { useTenantOptionalService } from './common/useServiceFactory'

import { ActiveTenantContext } from '@/context/common/Context'
import { queryKeys } from '@/lib/queryKeys'
import { BookingService } from '@/services/Bookings.service'

interface UseCustomerBookingsReturn {
  bookings: BaseBookingModel[]
  isLoading: boolean
  isError: boolean
  error: Error | null
}

/**
 * Hook for fetching bookings for a specific customer
 *
 * @param customerId - The ID of the customer
 * @returns Customer bookings query state
 */
export const useCustomerBookings = (customerId?: string): UseCustomerBookingsReturn => {
  const { tenantId } = useContext(ActiveTenantContext)

  const getService = useTenantOptionalService(BookingService)

  const bookingsQuery = useQuery({
    queryKey: queryKeys.customers.bookings(tenantId || '', customerId || ''),
    queryFn: async () => {
      if (!customerId) return []
      const service = await getService()
      return service.getCustomerBookings(customerId)
    },
    enabled: !!tenantId && !!customerId,
    staleTime: 2 * 60 * 1000,
  })

  useErrorHandler(bookingsQuery.isError, 'Fehler beim Laden der Kundenbuchungen.')

  return {
    bookings: bookingsQuery.data || [],
    isLoading: bookingsQuery.isLoading,
    isError: bookingsQuery.isError,
    error: bookingsQuery.error,
  }
}
